import { FC } from 'react';

import classNames from 'classnames';
import { getActiveCropTypes } from 'utils/clickme';
import { BANNER_IMAGE_CROP_TYPES, BannerImageCropType, ImageComponent } from 'utils/constants/banner';

import campaignCreateCropReplaceButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/common/campaign_create_crop_replace_button_click';
import { Button, Text, VSpacing } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import { setBannerImageComponentByType, setIsCropImageModalVisible } from 'store/models/createBanner';
import { setIsGalleryModalVisible } from 'store/models/gallery';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/common/BannerImg/UploadedImage/UploadedImage.module.less';

const LOGO_CLASS = {
    [BannerImageCropType.HH]: classNames(styles.uploadedLogo, styles.uploadedLogoHh),
    [BannerImageCropType.VK]: classNames(styles.uploadedLogo, styles.uploadedLogoVk),
    [BannerImageCropType.YandexAndVK]: classNames(styles.uploadedLogo, styles.uploadedLogoYandexAndVk),
};

const TrlKeys = {
    title: 'AdCreate.AdForm.BannerImg.title.notLoaded',
    change: 'AdCreate.AdForm.BannerImg.UploadedImage.title.changeMagritte',
    upload: 'AdCreate.AdForm.BannerImg.UploadedImage.uploadMagritte',
    sizes: {
        [BannerImageCropType.HH]: 'AdCreate.AdForm.CropImage.sizes.TARGET_DEFAULT',
        [BannerImageCropType.VK]: 'AdCreate.AdForm.CropImage.sizes.MY_TARGET_DEFAULT',
        [BannerImageCropType.YandexAndVK]: 'AdCreate.AdForm.CropImage.sizes.VK_DEFAULT',
    },
};

export const UploadedImage: FC = () => {
    const { bannerId, images, campaignIdForBanner } = useSelector((state) => state.createBanner);
    const { adSource } = useSelector((state) => state.createCampaign);
    const dispatch = useDispatch();

    const trl = useTrls();

    const currentBannerImageCropTypes = campaignIdForBanner ? getActiveCropTypes(adSource) : BANNER_IMAGE_CROP_TYPES;
    const isBelowMaxCrops = currentBannerImageCropTypes.length < BANNER_IMAGE_CROP_TYPES.length;

    const uploadNewImage = (cropType: BannerImageCropType) => {
        campaignCreateCropReplaceButtonClick({ bannerImageCropType: cropType });

        dispatch([
            setBannerImageComponentByType({
                cropType,
                component: ImageComponent.Loading,
            }),
            setIsGalleryModalVisible(true),
        ]);
    };

    return (
        <>
            <Text typography="label-2-regular" style="secondary">
                {trl(TrlKeys.title)}
            </Text>
            <VSpacing default={12} />
            <div className={styles.cropsContainer}>
                {currentBannerImageCropTypes.map((cropType) => {
                    if (bannerId && !images[cropType].url) {
                        return null;
                    }
                    return (
                        <div
                            key={cropType}
                            className={
                                isBelowMaxCrops ? styles.cropContainerItemBelowMaxCrops : styles.cropContainerItem
                            }
                        >
                            <div className={LOGO_CLASS[cropType]} />
                            <VSpacing default={12} />
                            <div className={styles.crop}>
                                {images[cropType].url ? (
                                    <>
                                        <img src={images[cropType].url} className={styles.cropImage} />
                                        <div
                                            className={styles.editButton}
                                            onClick={() => dispatch(setIsCropImageModalVisible(true))}
                                            data-qa="uploaded-image-edit"
                                        >
                                            <Button
                                                mode="secondary"
                                                size="small"
                                                style="accent"
                                                data-qa="uploaded-image-edit-button"
                                            >
                                                {trl(TrlKeys.change)}
                                            </Button>
                                        </div>
                                    </>
                                ) : (
                                    <Button
                                        mode="secondary"
                                        size="small"
                                        style="accent"
                                        data-qa="uploaded-image-upload-button"
                                        onClick={() => {
                                            uploadNewImage(cropType);
                                        }}
                                    >
                                        {trl(TrlKeys.upload)}
                                    </Button>
                                )}
                            </div>
                            <VSpacing default={12} />
                            <Text style="secondary">{trl(TrlKeys.sizes[cropType])}</Text>
                        </div>
                    );
                })}
            </div>
        </>
    );
};
